import React from 'react';
import { 
  User, Phone, Mail, CalendarDays, Stethoscope, 
  CreditCard, FileText, Building, CheckCircle, Clock, Eye 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { formatCurrency, calculateTotal, calculateSubtotal, OJO_OPTIONS, formatDateDisplay } from './constants';

const PatientDetail = ({ 
  isOpen, 
  onClose, 
  patient, 
  onEdit,
  hasRole 
}) => {
  if (!patient) return null;

  // Get examenes array or create from legacy fields
  const examenes = patient.examenes && patient.examenes.length > 0 
    ? patient.examenes 
    : patient.tipo_examen ? [{ 
        tipo_examen: patient.tipo_examen, 
        ojo_estudiar: patient.ojo_estudiar, 
        precio: patient.precio 
      }] : [];

  const subtotal = examenes.length > 0 ? calculateSubtotal(examenes) : patient.precio;
  const total = calculateTotal(patient.precio, patient.tipo_descuento, patient.descuento, examenes.length > 0 ? examenes : null);

  const getOjoLabel = (value) => {
    const option = OJO_OPTIONS.find(o => o.value === value);
    return option ? option.label : value;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="patient-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <User className="w-5 h-5 text-[#1a5192]" />
            Detalle del Paciente
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Personal Info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">{patient.nombre}</h3>
              <Badge className={patient.enviado 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-amber-100 text-amber-700'
              }>
                {patient.enviado ? (
                  <><CheckCircle className="w-3 h-3 mr-1" />Enviado</>
                ) : (
                  <><Clock className="w-3 h-3 mr-1" />Pendiente</>
                )}
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-slate-400" />
                <span><strong>Fecha:</strong> {formatDateDisplay(patient.fecha)}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span><strong>Edad:</strong> {patient.edad} años</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                <span><strong>Tel:</strong> {patient.telefono}</span>
              </div>
              {patient.correo && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span>{patient.correo}</span>
                </div>
              )}
              {patient.nit && (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span><strong>NIT:</strong> {patient.nit}</span>
                </div>
              )}
              {patient.institucion && (
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-400" />
                  <span><strong>Institución:</strong> {patient.institucion}</span>
                </div>
              )}
            </div>

            {/* IGSS Fields */}
            {patient.institucion === 'IGSS' && (patient.igss_afiliacion || patient.igss_codigo || patient.igss_nog || patient.igss_spg || patient.igss_unidad_referente) && (
              <div className="pt-3 border-t border-slate-200">
                <p className="text-xs font-medium text-slate-500 uppercase mb-2">Datos IGSS</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                  {patient.igss_afiliacion && (
                    <div>
                      <span className="text-slate-500">Afiliación:</span> {patient.igss_afiliacion}
                    </div>
                  )}
                  {patient.igss_codigo && (
                    <div>
                      <span className="text-slate-500">Código IGSS:</span> {patient.igss_codigo}
                    </div>
                  )}
                  {patient.igss_nog && (
                    <div>
                      <span className="text-slate-500">NOG:</span> {patient.igss_nog}
                    </div>
                  )}
                  {patient.igss_spg && (
                    <div>
                      <span className="text-slate-500">SPS-465:</span> {patient.igss_spg}
                    </div>
                  )}
                  {patient.igss_unidad_referente && (
                    <div>
                      <span className="text-slate-500">Unidad:</span> {patient.igss_unidad_referente}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Exams Info - Multiple Exams */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-blue-800 flex items-center gap-2">
              <Stethoscope className="w-4 h-4" />
              Exámenes Realizados ({examenes.length})
            </h4>
            
            {/* Exams List */}
            <div className="space-y-2">
              {examenes.map((exam, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg p-3 border border-blue-200 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium text-slate-800">{exam.tipo_examen}</p>
                      {exam.ojo_estudiar && (
                        <p className="text-xs text-blue-600 flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {getOjoLabel(exam.ojo_estudiar)}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="font-semibold text-blue-800">{formatCurrency(exam.precio)}</span>
                </div>
              ))}
            </div>

            {/* Subtotal */}
            {examenes.length > 1 && (
              <div className="pt-2 border-t border-blue-200 flex justify-between items-center">
                <span className="text-blue-600 text-sm">Subtotal Exámenes:</span>
                <span className="font-semibold text-blue-800">{formatCurrency(subtotal)}</span>
              </div>
            )}

            {/* Referring Doctor */}
            {patient.medico_referente && (
              <div className="pt-3 border-t border-blue-200">
                <span className="text-blue-600 text-sm">Médico Referente:</span>
                <p className="font-medium text-slate-800">{patient.medico_referente}</p>
              </div>
            )}
          </div>

          {/* Payment Info */}
          <div className="bg-emerald-50 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Información de Pago
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-emerald-600">Subtotal:</span>
                <p className="font-medium text-slate-800">{formatCurrency(subtotal)}</p>
              </div>
              {patient.tipo_descuento !== 'sin_descuento' && (
                <div>
                  <span className="text-emerald-600">Descuento:</span>
                  <p className="font-medium text-slate-800">
                    {patient.tipo_descuento === 'porcentaje' 
                      ? `${patient.descuento}%` 
                      : formatCurrency(patient.descuento)
                    }
                  </p>
                </div>
              )}
              <div>
                <span className="text-emerald-600">Forma de Pago:</span>
                <p className="font-medium text-slate-800">{patient.forma_pago}</p>
              </div>
              {patient.no_autorizacion && (
                <div>
                  <span className="text-emerald-600">No. Autorización:</span>
                  <p className="font-medium text-slate-800">{patient.no_autorizacion}</p>
                </div>
              )}
            </div>
            <div className="pt-3 border-t border-emerald-200 flex justify-between items-center">
              <span className="text-emerald-700 font-medium">Total a Pagar:</span>
              <span className="text-2xl font-bold text-emerald-700">{formatCurrency(total)}</span>
            </div>
          </div>

          {/* Notes */}
          {patient.observaciones && (
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2">Observaciones</h4>
              <p className="text-sm text-amber-700">{patient.observaciones}</p>
            </div>
          )}

          {/* Actions */}
          {hasRole(['SUPERADMIN', 'ADMIN', 'RECEPTIONIST']) && (
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button 
                variant="outline"
                onClick={onClose}
              >
                Cerrar
              </Button>
              <Button 
                onClick={onEdit}
                className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white"
              >
                Editar Paciente
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PatientDetail;
